import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home,
  Users,
  Calendar,
  ChevronDown,
  Check,
  Info,
  X,
  ArrowRightLeft,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatGuestDate, nightsBetween } from './guestDates';

interface UnitBookingInfo {
  bookingId: string;
  unitId: string;
  unitName: string;
  checkIn: string;
  checkOut: string;
  guestsCount: number;
  adultsCount?: number;
  childrenCount?: number;
  infantsCount?: number;
}

interface UnitSwitcherProps {
  units: UnitBookingInfo[];
  activeUnitId: string;
  onSwitch: (unitId: string) => void;
  darkMode?: boolean;
  className?: string;
}

const ONBOARDING_KEY = 'bookiapp_unit_switcher_seen';

// Gradient colors per unit index
const UNIT_GRADIENTS = [
  { from: 'from-blue-500', to: 'to-cyan-400', bg: 'bg-blue-500', ring: 'ring-blue-400' },
  { from: 'from-cyan-500', to: 'to-blue-400', bg: 'bg-cyan-500', ring: 'ring-cyan-400' },
  { from: 'from-emerald-500', to: 'to-teal-400', bg: 'bg-emerald-500', ring: 'ring-emerald-400' },
  { from: 'from-rose-500', to: 'to-pink-400', bg: 'bg-rose-500', ring: 'ring-rose-400' },
  { from: 'from-amber-500', to: 'to-orange-400', bg: 'bg-amber-500', ring: 'ring-amber-400' },
];

export function UnitSwitcher({
  units,
  activeUnitId,
  onSwitch,
  darkMode = true,
  className,
}: UnitSwitcherProps) {
  const { t, i18n } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const activeUnit = units.find((u) => u.unitId === activeUnitId);
  const activeIndex = units.findIndex((u) => u.unitId === activeUnitId);
  const activeGradient = UNIT_GRADIENTS[activeIndex % UNIT_GRADIENTS.length];

  // Check if user has seen the onboarding tooltip
  useEffect(() => {
    if (units.length > 1) {
      const hasSeen = localStorage.getItem(ONBOARDING_KEY);
      if (!hasSeen) {
        const timer = setTimeout(() => {
          setShowOnboarding(true);
        }, 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [units.length]);

  const dismissOnboarding = () => {
    setShowOnboarding(false);
    localStorage.setItem(ONBOARDING_KEY, 'true');
  };

  // Booking dates are local calendar days (see guestDates.ts — no UTC shift).
  const formatDate = (dateStr: string) => formatGuestDate(dateStr, i18n.language);

  const getNights = (checkIn: string, checkOut: string) => nightsBetween(checkIn, checkOut);

  // Don't render if there's only one unit
  if (units.length <= 1) {
    return null;
  }

  return (
    <div className={cn('relative', className)}>
      {/* Onboarding Tooltip */}
      <AnimatePresence>
        {showOnboarding && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -10 }}
            className="absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full z-50 w-[calc(100vw-2rem)] max-w-72"
          >
            <div className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white p-4 rounded-2xl shadow-2xl shadow-cyan-500/30 relative">
              <button
                onClick={dismissOnboarding}
                className="absolute top-2 right-2 p-1 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
              <div className="flex items-start gap-3">
                <div className="p-1.5 bg-white/20 rounded-lg">
                  <Info className="h-4 w-4" />
                </div>
                <div>
                  <p className="font-semibold mb-1 text-sm">
                    {t('guest.unitSwitcher.onboardingTitle', 'Imate više smještajnih jedinica!')}
                  </p>
                  <p className="text-xs text-blue-100 leading-relaxed">
                    {t(
                      'guest.unitSwitcher.onboardingText',
                      'Dodirnite ovdje za prebacivanje između svojih rezerviranih jedinica.'
                    )}
                  </p>
                </div>
              </div>
              {/* Arrow */}
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full">
                <div className="border-8 border-transparent border-t-blue-600" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Card */}
      <motion.div
        layout
        className={cn(
          'relative overflow-hidden rounded-2xl transition-all',
          darkMode
            ? 'bg-white/[0.06] backdrop-blur-xl border border-white/10'
            : 'bg-white/70 backdrop-blur-xl border border-gray-200/60 shadow-xl shadow-gray-200/40',
          showOnboarding && (darkMode ? 'ring-2 ring-blue-400/60' : 'ring-2 ring-blue-500/40'),
        )}
      >
        {/* Decorative gradient blob */}
        <div className={cn(
          'absolute -top-12 -right-12 w-32 h-32 rounded-full blur-3xl opacity-30',
          `bg-gradient-to-br ${activeGradient.from} ${activeGradient.to}`,
        )} />
        <div className={cn(
          'absolute -bottom-8 -left-8 w-24 h-24 rounded-full blur-2xl opacity-20',
          `bg-gradient-to-br ${activeGradient.from} ${activeGradient.to}`,
        )} />

        {/* Header - Always visible */}
        <button
          onClick={() => {
            setIsExpanded(!isExpanded);
            if (showOnboarding) dismissOnboarding();
          }}
          className="w-full relative z-10 p-4 flex items-center justify-between group"
        >
          <div className="flex items-center gap-3 min-w-0">
            {/* Active unit icon with gradient */}
            <div className={cn(
              'relative p-2.5 rounded-xl shadow-lg',
              `bg-gradient-to-br ${activeGradient.from} ${activeGradient.to}`,
              darkMode ? 'shadow-blue-500/20' : 'shadow-blue-500/30',
            )}>
              <Home className="h-5 w-5 text-white" />
              {/* Pulse ring */}
              <div className={cn(
                'absolute inset-0 rounded-xl animate-ping opacity-20',
                activeGradient.bg,
              )} style={{ animationDuration: '3s' }} />
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className={cn(
                  'font-bold text-base truncate',
                  darkMode ? 'text-white' : 'text-gray-900',
                )}>
                  {activeUnit?.unitName}
                </span>
                {/* Unit count pill */}
                <span className={cn(
                  'text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0',
                  `bg-gradient-to-r ${activeGradient.from} ${activeGradient.to}`,
                  'text-white shadow-sm',
                )}>
                  {units.length} {t('guest.unitSwitcher.units', 'jed.')}
                </span>
              </div>
              <div className={cn(
                'text-xs flex items-center gap-1.5 mt-0.5',
                darkMode ? 'text-white/50' : 'text-gray-500',
              )}>
                <ArrowRightLeft className="h-3 w-3" />
                <span>{t('guest.unitSwitcher.tapToSwitch', 'Dodirnite za prebacivanje')}</span>
              </div>
            </div>
          </div>

          {/* Chevron with rotation */}
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className={cn(
              'p-2 rounded-xl transition-colors',
              darkMode
                ? 'text-white/40 group-hover:text-white/70 group-hover:bg-white/5'
                : 'text-gray-400 group-hover:text-gray-600 group-hover:bg-gray-100',
            )}
          >
            <ChevronDown className="h-5 w-5" />
          </motion.div>
        </button>

        {/* Expanded Unit Cards */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
            >
              <div className={cn(
                'px-3 pb-3 space-y-2',
                darkMode ? 'border-t border-white/5' : 'border-t border-gray-100',
              )}>
                {/* Small divider line */}
                <div className="pt-2" />

                {units.map((unit, index) => {
                  const isActive = unit.unitId === activeUnitId;
                  const gradient = UNIT_GRADIENTS[index % UNIT_GRADIENTS.length];
                  const nights = getNights(unit.checkIn, unit.checkOut);

                  return (
                    <motion.button
                      key={unit.unitId}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      onClick={() => {
                        onSwitch(unit.unitId);
                        setIsExpanded(false);
                      }}
                      className={cn(
                        'w-full p-3 rounded-xl flex items-center gap-3 transition-all',
                        isActive
                          ? darkMode
                            ? 'bg-white/10 ring-1 ring-white/20'
                            : 'bg-gradient-to-r from-cyan-50 to-blue-50 ring-1 ring-cyan-200/60'
                          : darkMode
                            ? 'hover:bg-white/5'
                            : 'hover:bg-gray-50',
                      )}
                    >
                      {/* Unit indicator dot/check */}
                      <div className={cn(
                        'w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-all',
                        isActive
                          ? `bg-gradient-to-br ${gradient.from} ${gradient.to} shadow-lg`
                          : darkMode
                            ? 'bg-white/5 border border-white/10'
                            : 'bg-gray-100 border border-gray-200',
                      )}>
                        {isActive ? (
                          <Check className="h-4 w-4 text-white" />
                        ) : (
                          <Home className={cn(
                            'h-4 w-4',
                            darkMode ? 'text-white/30' : 'text-gray-400',
                          )} />
                        )}
                      </div>

                      {/* Unit details */}
                      <div className="flex-1 text-left min-w-0">
                        <p className={cn(
                          'font-semibold text-sm truncate',
                          isActive
                            ? darkMode ? 'text-white' : 'text-blue-700'
                            : darkMode ? 'text-white/70' : 'text-gray-700',
                        )}>
                          {unit.unitName}
                        </p>
                        <div className={cn(
                          'flex items-center gap-2 mt-0.5 text-xs',
                          darkMode ? 'text-white/40' : 'text-gray-500',
                        )}>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(unit.checkIn)} - {formatDate(unit.checkOut)}
                          </span>
                          <span className={cn(
                            'w-1 h-1 rounded-full',
                            darkMode ? 'bg-white/20' : 'bg-gray-300',
                          )} />
                          <span>{nights} {t('guest.payment.nights', 'noći')}</span>
                        </div>
                      </div>

                      {/* Guest count badge */}
                      <div className={cn(
                        'flex items-center gap-1 text-xs px-2 py-1 rounded-lg shrink-0',
                        isActive
                          ? darkMode
                            ? 'bg-white/10 text-white/80'
                            : 'bg-blue-100 text-blue-600'
                          : darkMode
                            ? 'bg-white/5 text-white/30'
                            : 'bg-gray-100 text-gray-500',
                      )}>
                        <Users className="h-3 w-3" />
                        <span className="font-medium">{unit.guestsCount}</span>
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

export default UnitSwitcher;
